import React, { type FC, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import {
	APPROVALS_ANALYTICS_SOURCE,
	computeReviewStatusCounts,
	getFailureStatusCode,
} from '../analytics.ts';
import { useApprovals, useApprovalsDispatch } from '../ApprovalsContext.tsx';
import { ApprovalActionConfirmationModal } from '../Modals/ApprovalActionConfirmationModal.tsx';
import { PressableLink } from '../PressableLink.tsx';

const i18n = defineMessages({
	approvalInProgressBannerPrefix: {
		id: 'approvals.editor.approval-in-progress.banner.prefix',
		description:
			'Prefix of banner message when user is on edit page but approval is pending - editing is disabled.',
		defaultMessage: 'Reminder: editing is locked while an approval is pending.',
	},
	approvalInProgressBannerPrefixImproved: {
		id: 'approvals.editor.approval-in-progress.banner.prefix.improved',
		description:
			'Banner message when user is on edit page but approval is pending - editing is disabled.',
		defaultMessage: 'Editing is locked while an approval is pending.',
	},
	approvalInProgressCancelCta: {
		id: 'approvals.editor.approval-in-progress.banner.cancel-cta',
		description:
			'CTA link to cancel the approval when editing is locked due to a pending approval.',
		defaultMessage: 'Cancel approval',
	},
	approvedBannerPrefix: {
		id: 'approvals.editor.approved.banner.prefix',
		description:
			'Prefix of banner message when user is on edit page but page is approved - editing is disabled.',
		defaultMessage: 'Reminder: You cannot edit an approved page without clearing the approval.',
	},
	approvedBannerPrefixImproved: {
		id: 'approvals.editor.approved.banner.prefix.improved',
		description:
			'Banner message when user is on edit page but page is approved - editing is disabled.',
		defaultMessage: 'You cannot edit an approved page without clearing the approval.',
	},
	approvedClearCta: {
		id: 'approvals.editor.approved.banner.clear-cta',
		description: 'CTA link to clear the approval when editing is locked due to an approved review.',
		defaultMessage: 'Clear approval',
	},
});

const styles = cssMap({
	banner: {
		height: '40px',
		paddingBlock: token('space.050'),
		paddingInline: token('space.250'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

const TRIGGER = 'banner';

/**
 * When approval is in progress (isDraftLockedForEditing), shows a banner explaining that editing
 * is not allowed. Renders nothing when there is no approval in progress or when changes are
 * requested (DENIED status) — in that case editing is permitted.
 *
 * - PENDING: "Reminder: editing is locked while an approval is pending. Cancel approval" (old, gate off)
 * - PENDING: "Editing is locked while an approval is pending. Cancel approval" (new, gate on)
 * - APPROVED: "Reminder: You cannot edit an approved page without clearing the approval. Clear approval."
 * - DENIED / no lock: renders nothing
 */
export const ApprovalInProgressEditorBlocker: FC = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		isDraftLockedForEditing,
		approvalRequest,
		isCurrentUserRequestor,
		isCurrentUserApprover,
		contentId,
	} = useApprovals();
	const { cancelWorkflowApplication } = useApprovalsDispatch();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

	const analyticsAttributes = useMemo(() => {
		const approvers = approvalRequest?.review?.approvers ?? [];
		return {
			reviewId: approvalRequest?.review?.reviewId,
			applicationId: approvalRequest?.application?.applicationId,
			contentId,
			isRequestor: isCurrentUserRequestor,
			isApprover: isCurrentUserApprover,
			isReviewPending: approvalRequest?.review?.reviewStatus === 'PENDING',
			currentReviewStatus: computeReviewStatusCounts(approvers),
			trigger: TRIGGER,
		};
	}, [approvalRequest, contentId, isCurrentUserApprover, isCurrentUserRequestor]);

	const reviewStatus = approvalRequest?.review?.reviewStatus;
	const isPending = reviewStatus === 'PENDING';
	const modalVariant = isPending ? 'cancelApproval' : 'clearApproval';

	const handleOpenConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(true);
	}, []);

	const handleConfirm = useCallback(async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'cancelApproval',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: analyticsAttributes,
			},
		}).fire();
		setIsConfirmModalOpen(false);

		try {
			await cancelWorkflowApplication();
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'canceled',
					actionSubject: 'approvalWorkflowReview',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: analyticsAttributes,
				},
			}).fire();
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'failed',
					actionSubject: 'approvalWorkflowReview',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: {
						...analyticsAttributes,
						failureStatusCode: getFailureStatusCode(error),
					},
				},
			}).fire();
		}
	}, [analyticsAttributes, cancelWorkflowApplication, createAnalyticsEvent]);

	const handleClose = useCallback(() => {
		setIsConfirmModalOpen(false);
	}, []);

	if (!isDraftLockedForEditing) {
		return null;
	}

	return (
		<>
			{reviewStatus === 'APPROVED' ? (
				<Flex xcss={styles.banner} alignItems="center" justifyContent="center" gap="space.050">
					<Text>
						{formatMessage(
							fg('confluence_approvals_improvements_batch_1')
								? i18n.approvedBannerPrefixImproved
								: i18n.approvedBannerPrefix,
						)}
					</Text>
					<PressableLink onClick={handleOpenConfirmModal}>
						{formatMessage(i18n.approvedClearCta)}
					</PressableLink>
				</Flex>
			) : (
				// reviewStatus === 'PENDING' (the only remaining locked state)
				<Flex xcss={styles.banner} alignItems="center" justifyContent="center" gap="space.050">
					<Text>
						{formatMessage(
							fg('confluence_approvals_improvements_batch_1')
								? i18n.approvalInProgressBannerPrefixImproved
								: i18n.approvalInProgressBannerPrefix,
						)}
					</Text>
					<PressableLink onClick={handleOpenConfirmModal}>
						{formatMessage(i18n.approvalInProgressCancelCta)}
					</PressableLink>
				</Flex>
			)}
			{isConfirmModalOpen && (
				<ApprovalActionConfirmationModal
					variant={modalVariant}
					onConfirm={handleConfirm}
					onClose={handleClose}
				/>
			)}
		</>
	);
};
